import React, { Component } from 'react'
import { extendObservable } from 'mobx'
import { observer, inject } from 'mobx-react'
import PropTypes from 'prop-types'
import CopyButton from '@components/CopyButton'
import Collapse from '@material-ui/core/Collapse'
import IconButton from '@material-ui/core/IconButton'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import Button from '@material-ui/core/Button'
import Spinner from '@components/spinner'
import { projectApi, eplApi } from '@root/../../packages/platform-api/dist'

class ClientProjectLinkModal extends Component {
  static propTypes = {
    modalStore: PropTypes.object.isRequired,
    growlStore: PropTypes.object.isRequired,
    externLinks: PropTypes.object.isRequired,
    projectId:  PropTypes.number,
    projectName:  PropTypes.string,
    eplId:  PropTypes.number,
  }
  constructor (props) {
    super(props)
    extendObservable(this, {
      title: '',
      content: '',
      sendEmail: null,
      clientId: null,
      sendingInProgress: false
    })
  }

  confirmSendAllIndividualLinks = () => {
    this.title = "Send an email with an individual link to all client contacts"
    this.clientId = null
    if(this.props.projectId) {
      this.content = contentForIndividualProjectLinks(this.props.projectName)
    } else {
      this.content = contentForIndividualEplLinks(this.props.projectName)
    }
    this.sendEmail = this.sendIndividualLink
  }

  confirmSendIndividualLink = (client) => {
    this.title = "Send an email with an individual link to " + client.name
    this.clientId = client.clientId
    if(this.props.projectId) {
      this.content = contentForIndividualProjectLink({clientName: client.name, projectName: this.props.projectName, link: client.path})
    } else {
      this.content = contentForIndividualEplLink({clientName: client.name, projectName: this.props.projectName, link: client.path})
    }
    this.sendEmail = this.sendIndividualLink
  }

  confirmSendGenericLinks = () => {
    this.title = "Send an email with generic link to all client contacts"
    if(this.props.projectId) {
      this.content = contentForGenericProjectLink(this.props.projectName)
    } else {
      this.content = contentForGenericEplLink(this.props.projectName)
    }
    this.sendEmail = this.sendGenericLinks
  }
  
  sendIndividualLink = async () => {
    this.sendingInProgress = true
    try {
      if(this.props.projectId) {
        await projectApi.sendIndividualLink(this.props.projectId, this.clientId)
      } else {
        await eplApi.sendIndividualLink(this.props.eplId, this.clientId)
      }

      this.props.growlStore.showSuccess('Email sent.')
      this.resetConfirmation()
      this.sendingInProgress = false
    } catch(err) {
      this.sendingInProgress = false
      this.props.growlStore.showError('Something went wrong. Please try again.')
    }
  }

  sendGenericLinks = async () => {
    this.sendingInProgress = true
    try {
      if(this.props.projectId) {
        await projectApi.sendGenericLinks(this.props.projectId)
      } else {
        await eplApi.sendGenericLinks(this.props.eplId)
      }
      this.props.growlStore.showSuccess('Emails sent.')
      this.resetConfirmation()
      this.sendingInProgress = false
    } catch(err) {
      this.sendingInProgress = false
      this.props.growlStore.showError('Something went wrong. Please try again.')
    }
  }
  
  resetConfirmation = () => {
    this.title = ''
    this.content = ''
    this.clientId = ''
    this.sendEmail = null
  }

  render() {
    if(this.sendingInProgress) return <Spinner />

    const genericLink = this.props.externLinks[0].path.includes('?') ? this.props.externLinks[0].path.split('?')[0] : this.props.externLinks[0].path
    
    return (<div>
      {this.title && this.content && this.sendEmail ? (
      <div className="email-confirmation">
        <div className='title'>{this.title}</div>
        <div className='content' dangerouslySetInnerHTML={{ __html: this.content }}></div>
        <Button 
          variant='outlined' 
          color="primary"
          onClick={this.resetConfirmation}>Cancel</Button>
        <Button 
          className='right'
          variant='contained' 
          color="primary"
          onClick={this.sendEmail}>Send</Button>
      </div>
      ) : (
      <div className="client-links">
        <div className="title">
          Personalized links
          <MailOutlineIcon onClick={this.confirmSendAllIndividualLinks} />
        </div>
        <div className="links">
          {this.props.externLinks.map(link => (
            <LinkRow name={link.name} path={link.path} sendLink={() => this.confirmSendIndividualLink(link)} />
          ))}
        </div>
        <hr />
        <div className="generic-link">
          <LinkRow name="Generic link" path={genericLink} sendLink={this.confirmSendGenericLinks}/>
        </div>
      </div>
      )}
      </div>
    )
  }
}

function LinkRow(props) {
  const { name, path, sendLink } = props
  const [open, setOpen] = React.useState(false)

  return (
    <div className='link-wrap'>
        <div className='client-name'>
            {name}
            <div>
              <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
              <MailOutlineIcon onClick={sendLink} />
              <CopyButton value={path} />
            </div>
        </div>
        <Collapse in={open} timeout="auto" unmountOnExit>
            {path}
        </Collapse>
    </div>)
}

const contentForIndividualProjectLink = (props) => {
  const {clientName, projectName, link} = props
  return `<b>Subject:</b> Your Access Link to ${projectName}<br /><br />
    Dear ${clientName}, <br /><br />
    To start using the client portal for project ${projectName}, please click on your <a href="${link}">individual link</a>. Please do not share your individual access link to others.<br /><br />
    Kind regards,<br />
    Atheneum Team`
}

const contentForIndividualProjectLinks = (projectName) => {
  return `<b>Subject:</b> Your Access Link to ${projectName}<br /><br />
    Dear {{ Client Name }}, <br /><br />
    To start using the client portal for project ${projectName}, please click on your {{individual link}}. Please do not share your individual access link to others.<br /><br />
    Kind regards,<br />
    Atheneum Team`
}


const contentForIndividualEplLink = (props) => {
  const {clientName, projectName, link} = props
  return `<b>Subject:</b> Your Access Link to Expert Profile<br /><br />
    Dear ${clientName},<br /><br />
    To view the profile of the expert submitted for ${projectName}, click on your <a href="${link}">individual link</a>. Please do not share your individual access link to others.<br /><br />
    Kind regards,<br />
    Atheneum Team`
}

const contentForIndividualEplLinks = (projectName) => {
  return `<b>Subject:</b> Your Access Link to Expert Profile<br /><br />
    Dear {{ Client Name }},<br /><br />
    To view the profile of the expert submitted for ${projectName}, click on your {{individual link}}. Please do not share your individual access link to others.<br /><br />
    Kind regards,<br />
    Atheneum Team`
}

const contentForGenericProjectLink = (projectName) => {
  return `<b>Subject:</b> Your Access Link to ${projectName}<br /><br />
  Dear {{Client Name}}>, <br /><br />
  To start using the client portal for project ${projectName}, please click on the {{generic link}}.<br /><br />
  Kind regards,<br />
  Atheneum Team`
}

const contentForGenericEplLink = (projectName) => {
  return `<b>Subject:</b> Your Access Link to Expert Profile<br /><br />
  Dear {{Client Name}},<br /><br />
  To view the profile of the expert submitted for ${projectName}, please click on the {{generic link}}.<br /><br />
  Kind regards,<br />
  Atheneum Team`
}

export default inject('modalStore', 'growlStore')(observer(ClientProjectLinkModal))
